import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'

const WorkspaceContext = createContext()

export function WorkspaceProvider({ children }) {
  const [workspaces, setWorkspaces] = useState([])
  const [currentWorkspace, setCurrentWorkspace] = useState(null)
  const [saldo, setSaldo] = useState({
    saldo_disponivel: 0,
    saldo_bloqueado: 0,
    saldo_a_receber: 0,
    total_recebido: 0,
    total_sacado: 0
  })
  const [loading, setLoading] = useState(true)

  // Buscar workspaces do usuário
  const fetchWorkspaces = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data, error } = await supabase
        .from('workspaces')
        .select('*')
        .eq('owner_id', user.id)
        .eq('ativo', true)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: true })

      if (error) throw error

      setWorkspaces(data || [])
      
      // Se não tem workspace atual ou o atual não está na lista
      if (data && data.length > 0) {
        const savedWorkspaceId = localStorage.getItem('currentWorkspaceId')
        const found = data.find(w => w.id === savedWorkspaceId)
        
        if (found) {
          setCurrentWorkspace(found)
        } else {
          // Usar o default ou o primeiro
          const defaultWorkspace = data.find(w => w.is_default) || data[0]
          setCurrentWorkspace(defaultWorkspace)
          localStorage.setItem('currentWorkspaceId', defaultWorkspace.id)
        }
      }
    } catch (error) {
      console.error('Erro ao buscar workspaces:', error)
    }
  }, [])

  // Buscar saldo do workspace
  const fetchSaldo = useCallback(async (workspaceId) => {
    if (!workspaceId) return
    
    try {
      const { data, error } = await supabase
        .from('workspace_saldos')
        .select('*')
        .eq('workspace_id', workspaceId)
        .single()

      if (error && error.code !== 'PGRST116') throw error

      if (data) {
        setSaldo(data)
      }
    } catch (error) {
      console.error('Erro ao buscar saldo:', error)
    }
  }, [])

  // Carregar workspaces ao iniciar
  useEffect(() => {
    fetchWorkspaces().then(() => setLoading(false))
  }, [fetchWorkspaces])

  // Atualizar saldo quando mudar workspace
  useEffect(() => {
    if (currentWorkspace?.id) {
      fetchSaldo(currentWorkspace.id)
    }
  }, [currentWorkspace?.id, fetchSaldo])

  // Trocar workspace
  const switchWorkspace = useCallback((workspace) => {
    setCurrentWorkspace(workspace)
    localStorage.setItem('currentWorkspaceId', workspace.id)
    // Recarregar a página para atualizar todos os dados
    window.location.reload()
  }, [])

  // Criar novo workspace
  const createWorkspace = useCallback(async (workspaceData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Usuário não autenticado')

      const { data, error } = await supabase
        .from('workspaces')
        .insert({
          ...workspaceData,
          owner_id: user.id
        })
        .select()
        .single()

      if (error) throw error

      await fetchWorkspaces()
      return data
    } catch (error) {
      console.error('Erro ao criar workspace:', error)
      throw error
    }
  }, [fetchWorkspaces])

  // Atualizar workspace
  const updateWorkspace = useCallback(async (workspaceId, updates) => {
    try {
      const { data, error } = await supabase
        .from('workspaces')
        .update(updates)
        .eq('id', workspaceId)
        .select()
        .single()

      if (error) throw error

      await fetchWorkspaces()
      if (currentWorkspace?.id === workspaceId) {
        setCurrentWorkspace(data)
      }
      return data
    } catch (error) {
      console.error('Erro ao atualizar workspace:', error)
      throw error
    }
  }, [fetchWorkspaces, currentWorkspace])

  // Definir workspace padrão
  const setDefaultWorkspace = useCallback(async (workspaceId) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Usuário não autenticado')

      // Remover default de todos
      await supabase
        .from('workspaces')
        .update({ is_default: false })
        .eq('owner_id', user.id)

      // Definir novo default
      const { error } = await supabase
        .from('workspaces')
        .update({ is_default: true })
        .eq('id', workspaceId)

      if (error) throw error

      await fetchWorkspaces()
    } catch (error) {
      console.error('Erro ao definir workspace padrão:', error)
      throw error
    }
  }, [fetchWorkspaces])

  // Realizar transferência (saque)
  const requestTransfer = useCallback(async (transferData) => {
    try {
      const { data, error } = await supabase
        .from('transferencias')
        .insert({
          ...transferData,
          workspace_id: currentWorkspace.id
        })
        .select()
        .single()

      if (error) throw error
      
      // Atualizar saldo
      await fetchSaldo(currentWorkspace.id)
      
      return data
    } catch (error) {
      console.error('Erro ao solicitar transferência:', error)
      throw error
    }
  }, [currentWorkspace, fetchSaldo])

  const value = {
    workspaces,
    currentWorkspace,
    saldo,
    loading,
    switchWorkspace,
    createWorkspace,
    updateWorkspace,
    setDefaultWorkspace,
    requestTransfer,
    refreshWorkspaces: fetchWorkspaces,
    refreshSaldo: () => fetchSaldo(currentWorkspace?.id)
  }

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  )
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext)
  if (!context) {
    throw new Error('useWorkspace deve ser usado dentro de WorkspaceProvider')
  }
  return context
}
